import { createFileRoute, Link } from "@tanstack/react-router";
import { Phone } from "lucide-react";
import { PageSection } from "@/components/site/PageSection";
import { LazyMapFrame } from "@/components/site/LazyMapFrame";
import { EsPageShell, EsSection } from "@/components/site/EsPageShell";
import { EsContactForm } from "@/components/site/EsContactForm";
import { esHead, esWebPageSchema, SITE } from "@/lib/es-pages";

const PAGE_URL = `${SITE}/es/cirugia-bariatrica-virginia`;

/** Spanish MedicalClinic node: same NAP data as the English LocalBusiness. */
const localSchema = {
  "@context": "https://schema.org",
  "@type": "MedicalClinic",
  "@id": `${PAGE_URL}#clinic`,
  name: "Virginia Bariatric Surgery — Dr. Matthew Fitzer",
  url: PAGE_URL,
  // Validators require image/logo and priceRange on a LocalBusiness/Organization
  // node; mirror the English ORGANIZATION_SCHEMA so both languages validate.
  logo: `${SITE}/logo.png`,
  image: `${SITE}/logo.png`,
  telephone: "+1-703-709-9771",
  faxNumber: "+1-888-519-0045",
  email: "Val@virginiabariatric.com",
  priceRange: "$$",
  currenciesAccepted: "USD",
  paymentAccepted: "Cash, Credit Card, Insurance",
  knowsLanguage: ["es", "en"],
  medicalSpecialty: "Surgical",

  address: {
    "@type": "PostalAddress",
    streetAddress: "171 Elden Street, Suite 3B",
    addressLocality: "Herndon",
    addressRegion: "VA",
    postalCode: "20170",
    addressCountry: "US",
  },
  geo: { "@type": "GeoCoordinates", latitude: 38.9695, longitude: -77.3861 },
  hasMap:

    "https://www.google.com/maps/search/?api=1&query=171+Elden+Street+Suite+3B+Herndon+VA+20170",
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "08:00",
      closes: "16:30",
    },
  ],
  areaServed: [
    { "@type": "City", name: "Herndon" },
    { "@type": "City", name: "Reston" },
    { "@type": "City", name: "Fairfax" },
    { "@type": "City", name: "Arlington" },
    { "@type": "City", name: "Alexandria" },
    { "@type": "City", name: "Manassas" },
    { "@type": "City", name: "Sterling" },
  ],
  parentOrganization: { "@id": `${SITE}/#organization` },
};

export const Route = createFileRoute("/es/cirugia-bariatrica-virginia")({
  head: () => {
    const base = esHead("local");
    return {
      ...base,
      scripts: import.meta.env.SSR ? [
        {
          type: "application/ld+json",
          children: JSON.stringify(esWebPageSchema("local")),
        },
        { type: "application/ld+json", children: JSON.stringify(localSchema) },
      ] : [],
    };
  },
  component: EsLocalPage,
});

function EsLocalPage() {
  return (
    <EsPageShell
      page="local"
      title="Cirugía bariátrica en Herndon, Virginia"
      intro="Nuestro consultorio atiende en español a pacientes de Herndon, Reston, Fairfax, Arlington, Alexandria, Sterling, Manassas y todo el norte de Virginia. Aquí encontrará la dirección, el horario, el teléfono y un formulario para pedir su cita."
    >
      <PageSection className="pb-6 grid gap-3 md:grid-cols-2">
        <div className="flex h-full flex-col gap-3">
          <aside className="flex">
            <div className="flex-1 rounded-2xl bg-brand-soft/50 p-5 md:p-6">
              <h2 className="font-serif text-2xl text-brand-deep">Dirección del consultorio</h2>
              <address className="not-italic mt-2 text-[0.9rem] leading-[1.4] text-foreground/80">
                Virginia Bariatric Surgery
                <br />
                171 Elden Street, Suite 3B
                <br />
                Herndon, VA 20170
              </address>
              <p className="mt-3 text-sm text-muted-foreground">
                Estacionamiento gratuito en el edificio y acceso sin escaleras.
              </p>
              <a
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
                href="tel:7037099771"
                aria-label="Llame a Virginia Bariatric Surgery al 703-709-9771"
              >
                <Phone size={16} aria-hidden="true" />
                (703) 709-9771
              </a>
            </div>
          </aside>

          <aside className="flex">
            <dl className="flex-1 divide-y divide-border rounded-2xl border border-border bg-card">
              <div className="flex items-baseline justify-between gap-4 p-4">
                <dt className="text-sm uppercase tracking-wider text-muted-foreground">Horario</dt>
                <dd className="text-right text-[0.9rem] text-foreground/80">
                  Lunes a viernes
                  <br />
                  8:00 a. m. – 4:30 p. m.
                </dd>
              </div>
              <div className="flex items-baseline justify-between gap-4 p-4">
                <dt className="text-sm uppercase tracking-wider text-muted-foreground">
                  Sábados y domingos
                </dt>
                <dd className="text-[0.9rem] text-foreground/80">Cerrado</dd>
              </div>
              <div className="flex items-baseline justify-between gap-4 p-4">
                <dt className="text-sm uppercase tracking-wider text-muted-foreground">Teléfono</dt>
                <dd className="text-[0.9rem]">
                  <a
                    className="font-medium text-brand-logo"
                    aria-label="Llame a Virginia Bariatric Surgery al 703-709-9771"
                    href="tel:7037099771"
                  >
                    (703) 709-9771
                  </a>
                </dd>
              </div>
              <div className="flex items-baseline justify-between gap-4 p-4">
                <dt className="text-sm uppercase tracking-wider text-muted-foreground">Fax</dt>
                <dd className="text-[0.9rem] text-foreground/80">(888) 519-0045</dd>
              </div>
              <div className="flex items-baseline justify-between gap-4 p-4">
                <dt className="text-sm uppercase tracking-wider text-muted-foreground">
                  Correo electrónico
                </dt>
                <dd className="text-[0.9rem]">
                  <a
                    className="break-words font-medium text-brand-logo"
                    href="mailto:Val@virginiabariatric.com"
                  >
                    Val@virginiabariatric.com
                  </a>
                </dd>
              </div>
            </dl>
          </aside>
        </div>

        <EsContactForm
          idPrefix="es-local"
          heading="Pida su cita en español"
        />
      </PageSection>

      <EsSection id="servicios" heading="Qué ofrecemos en Herndon">
        <p>
          El Dr. Matthew Fitzer es cirujano bariátrico certificado y ha realizado más de 6,000
          operaciones de pérdida de peso. En este consultorio atendemos consultas iniciales,
          estudios previos a la cirugía, seguimiento posoperatorio y el programa médico de
          pérdida de peso con medicamentos.
        </p>
        <ul className="list-disc space-y-1 pl-5">
          <li>Manga gástrica (gastrectomía en manga) por laparoscopía</li>
          <li>Bypass gástrico Roux-en-Y</li>
          <li>Balón gástrico Orbera® sin cirugía</li>
          <li>Cirugía de revisión después de banda gástrica o manga</li>
          <li>Medicamentos GLP-1 (Wegovy, Zepbound, Ozempic, Mounjaro) con supervisión médica</li>
        </ul>
        <p>
          <Link
            to="/es/cirugia-bariatrica-de-pago-directo"
            className="font-medium text-brand-logo hover:underline"
          >
            Vea los precios de pago propio de cada procedimiento
          </Link>{" "}
          si prefiere no usar el seguro.
        </p>
      </EsSection>


      <EsSection id="citas" heading="Cómo pedir una cita">
        <p>
          Llame al{" "}
          <a className="font-medium text-brand-logo" href="tel:7037099771">
            (703) 709-9771
          </a>{" "}
          durante el horario de atención o envíe el formulario de esta página. Indique que
          prefiere hablar en español y le devolvemos la llamada. Le pediremos su fecha de
          nacimiento, su estatura y peso actuales, y el nombre de su seguro médico para verificar
          la cobertura antes de la primera consulta.
        </p>
      </EsSection>

      <EsSection id="cerca-de-herndon" heading="Cirugía bariátrica cerca de Herndon, Reston y Fairfax">
        <p>
          El Dr. Fitzer atiende a sus pacientes en nuestro consultorio de 171 Elden Street, Suite 3B,
          Herndon, y opera en INOVA Fair Oaks Hospital en Fairfax. Los procedimientos endoscópicos
          (ESG, TORe y el balón Orbera), la cirugía de banda gástrica LAP-BAND y la reparación de
          hernia hiatal son casi siempre ambulatorios. La manga, el bypass y la mayoría de las
          revisiones suelen requerir una noche en el hospital. Llame al{" "}
          <a className="font-medium text-brand-logo" href="tel:7037099771">
            703-709-9771
          </a>{" "}
          para programar su cita.
        </p>
      </EsSection>

      <PageSection className="pb-6">
        <h2 className="text-center font-serif text-2xl text-brand-deep">
          Cómo llegar a nuestro consultorio en Herndon
        </h2>
        <p className="mt-2 text-center text-sm text-muted-foreground">
          Virginia Bariatric Surgery · 171 Elden Street, Suite 3B, Herndon, VA 20170
        </p>
        <div className="mt-4 overflow-hidden rounded-2xl border border-border">
          <LazyMapFrame
            title="Mapa de Google que muestra Virginia Bariatric Surgery en 171 Elden Street, Suite 3B, Herndon, VA 20170"
            src="https://www.google.com/maps?q=171%20Elden%20Street%20Suite%203B%2C%20Herndon%2C%20VA%2020170&output=embed"
          />
        </div>
        <p className="mt-3 text-center text-sm">
          <a
            className="font-medium text-brand-logo hover:underline"
            href="https://www.google.com/maps/search/?api=1&query=171+Elden+Street+Suite+3B+Herndon+VA+20170"
            target="_blank"
            rel="noreferrer"
          >
            Ver indicaciones para llegar a nuestro consultorio en Herndon
          </a>
        </p>
      </PageSection>
    </EsPageShell>
  );
}
